import React, { useEffect, useRef, useState } from "react";
import { Check, Trash2, Plus, ListPlus } from "lucide-react";
import { bookWait } from "../lib/bookWait";

const blank = () => ({ number: "", qty: "1", done: false });

/* Paper-slip style grid: N rows of [number][qty]. Qty is 1 by default — once the number is complete a 5-second
   countdown runs; type a qty (or press Enter / next) to book at once with that qty, else it books 1 SET itself. */
export const SheetEntry = ({ digits, onAddRow, label = "", initialRows = 10, testPrefix = "sheet" }) => {
  const [rows, setRows] = useState(() => Array.from({ length: initialRows }, blank));
  const [count, setCount] = useState({});          // row index → seconds left
  const refs = useRef({});
  const rowsRef = useRef(rows); rowsRef.current = rows;
  const timers = useRef({});
  const focus = (i, f) => setTimeout(() => { const el = refs.current[`${i}-${f}`]; el?.focus(); el?.select?.(); }, 20);
  const patch = (i, p) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const stop = (i) => { if (timers.current[i]) { clearInterval(timers.current[i]); delete timers.current[i]; } setCount((c) => { const n = { ...c }; delete n[i]; return n; }); };
  useEffect(() => () => Object.values(timers.current).forEach(clearInterval), []);

  const commit = (i, r = rowsRef.current[i]) => {
    stop(i);
    if (!r || r.done || r.number.length !== digits) return false;
    const q = Math.max(1, Math.round(Number(r.qty) || 1));
    const ok = onAddRow(r.number, q);
    if (ok !== false) { patch(i, { done: true, qty: String(q) }); return true; }
    return false;
  };
  const advance = (i) => { if (i + 1 >= rowsRef.current.length) setRows((r) => [...r, blank()]); focus(i + 1, "n"); };
  const schedule = (i) => {
    stop(i);
    let left = bookWait(); setCount((c) => ({ ...c, [i]: left }));
    timers.current[i] = setInterval(() => {
      left -= 1;
      if (left > 0) { setCount((c) => ({ ...c, [i]: left })); return; }
      if (commit(i)) advance(i);
    }, 1000);
  };
  const onNum = (i, v) => {
    const n = v.replace(/\D/g, "").slice(0, digits);
    patch(i, { number: n });
    if (n.length === digits) schedule(i); else stop(i);
  };
  const onQty = (i, v) => { stop(i); patch(i, { qty: v.replace(/\D/g, "").slice(0, 4) }); };
  const next = (i) => { if (commit(i)) advance(i); };
  const addAll = () => { let c = 0; rowsRef.current.forEach((r, i) => { if (commit(i, r)) c++; }); return c; };
  const pending = rows.filter((r) => !r.done && r.number.length === digits).length;
  const doneCount = rows.filter((r) => r.done).length;

  return (
    <div className="mt-3 rounded-xl border border-dashed border-amber-300 bg-amber-50/40 p-2 w-full min-w-0 overflow-hidden" data-testid={`${testPrefix}-grid`}>
      <div className="flex items-center justify-between mb-1 px-1">
        <div className="text-[11px] font-black text-slate-700">📝 {label || "Sheet"} · {digits}-digit × qty</div>
        <div className="text-[10px] text-slate-500">{doneCount} added</div>
      </div>
      <div className="grid grid-cols-[24px_minmax(0,1fr)_64px_32px] gap-1 items-center text-[10px] text-slate-500 px-1 mb-0.5"><span>#</span><span>Number</span><span>Qty</span><span /></div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className={`grid grid-cols-[24px_minmax(0,1fr)_64px_32px] gap-1 items-center ${r.done ? "opacity-60" : ""}`} data-testid={`${testPrefix}-row-${i}`}>
            <span className="text-[11px] font-bold text-slate-500 text-center">{i + 1}</span>
            <input ref={(el) => (refs.current[`${i}-n`] = el)} value={r.number} disabled={r.done} onChange={(e) => onNum(i, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); next(i); } }}
              inputMode="numeric" enterKeyHint="next" placeholder={"0".repeat(digits)} className="w-full min-w-0 rl-card px-2 py-1.5 text-sm font-mono font-bold outline-none disabled:bg-emerald-50" data-testid={`${testPrefix}-num-${i}`} />
            <input ref={(el) => (refs.current[`${i}-q`] = el)} value={r.qty} disabled={r.done} onFocus={(e) => { stop(i); e.target.select(); }} onChange={(e) => onQty(i, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); next(i); } }} onBlur={() => { if (r.number.length === digits) next(i); }}
              inputMode="numeric" enterKeyHint="next" placeholder="1" className="w-full min-w-0 rl-card px-2 py-1.5 text-sm font-mono font-bold outline-none disabled:bg-emerald-50" data-testid={`${testPrefix}-qty-${i}`} />
            {r.done ? <span className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center" data-testid={`${testPrefix}-done-${i}`}><Check className="w-4 h-4" /></span>
              : count[i] ? <button onClick={() => next(i)} className="w-8 h-8 rounded-lg bg-amber-500 text-white text-xs font-black flex items-center justify-center" title="Adding… tap to add now" data-testid={`${testPrefix}-count-${i}`}>{count[i]}</button>
              : (r.number || r.qty !== "1") ? <button onClick={() => { stop(i); patch(i, blank()); }} className="w-8 h-8 rounded-lg rl-card text-slate-400 flex items-center justify-center" data-testid={`${testPrefix}-clear-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
              : <span />}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <button onClick={() => setRows((r) => [...r, ...Array.from({ length: 10 }, blank)])} className="flex-1 py-2 rounded-xl text-xs font-black rl-card text-slate-700 flex items-center justify-center gap-1" data-testid={`${testPrefix}-more`}><Plus className="w-3.5 h-3.5" />10 more rows</button>
        <button onClick={addAll} disabled={!pending} className="flex-1 py-2 rounded-xl text-xs font-black bg-slate-900 text-white flex items-center justify-center gap-1 disabled:opacity-40" data-testid={`${testPrefix}-add-all`}><ListPlus className="w-3.5 h-3.5" />Add filled ({pending})</button>
      </div>
    </div>
  );
};
